import { c as _c } from "react/compiler-runtime";
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { selectIsResizing, selectResizingColumn } from '../store/createEagGridStore';
import { useEagGridSelector } from '../store/useEagGridSelector';
import { useEagGridStore } from '../store/useEagGridStore';
import { toIntegerWidth } from '../utils/toIntegerWidth';
const MIN_COLUMN_WIDTH = 50;
type UseColumnResizeReturn = {
  onResizeStart: (colKey: string, startX: number, colIndex: number, resizeFromLeft?: boolean) => void;
  isResizing: boolean;
  resizingColumn: string | null;
};

/**
 * Manages column resize interactions using **direct DOM updates** during drag.
 *
 * The hook owns the full pointer lifecycle: onResizeStart attaches document-level
 * `pointermove` and `pointerup` listeners that are removed automatically on resize end
 * or component unmount. This guarantees pointer events are always received even if the
 * cursor leaves the scroll container (edge case: fast drag).
 *
 * Performance design (Vercel rules: `rerender-use-ref-transient-values` + `js-batch-dom-css`):
 * - During drag: transient width stored in a ref, `<col>` element style updated directly.
 *   Zero React re-renders, zero store notifications.
 * - On pointer-up (primary button only): commits the final width to the store in a single
 *   `setColumnWidth` call + `endResizing` batch.
 *
 * Context menu is suppressed on `document` during the resize session to prevent the browser
 * menu from appearing on accidental right-click.
 *
 * @param colgroupRef — ref to the `<colgroup>` DOM element whose `<col>` children control column widths
 * @param getWidth — returns the current store width for a column key (used to capture startWidth)
 */
export const useColumnResize = (colgroupRef, getWidth) => {
  const $ = _c(11);
  const store = useEagGridStore();
  const isResizing = useEagGridSelector(selectIsResizing);
  const resizingColumn = useEagGridSelector(selectResizingColumn) ?? null;
  const colKeyRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const currentWidthRef = useRef(0);
  const activeColElementRef = useRef(null);
  const cleanupRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const removeDocumentListeners = t0;
  const resizeFromLeftRef = useRef(false);
  let t1;
  if ($[1] !== colgroupRef || $[2] !== getWidth || $[3] !== store) {
    t1 = (colKey, startX, colIndex, t2) => {
      const resizeFromLeft = t2 === undefined ? false : t2;
      removeDocumentListeners();
      const width = getWidth(colKey);
      colKeyRef.current = colKey;
      startXRef.current = startX;
      startWidthRef.current = width;
      currentWidthRef.current = width;
      resizeFromLeftRef.current = resizeFromLeft;
      activeColElementRef.current = colgroupRef.current?.children[colIndex] as HTMLElement | undefined ?? null;
      store.startResizing(colKey);
      const handlePointerMove = e => {
        if (!colKeyRef.current) {
          return;
        }
        const delta = e.clientX - startXRef.current;
        const effectiveDelta = resizeFromLeftRef.current ? -delta : delta;
        const newWidth = toIntegerWidth(Math.max(MIN_COLUMN_WIDTH, startWidthRef.current + effectiveDelta));
        currentWidthRef.current = newWidth;
        const colElement = activeColElementRef.current;
        if (colElement) {
          colElement.style.width = `${newWidth}px`;
        }
      };
      const handlePointerUp = e_0 => {
        if (e_0.button !== 0) {
          return;
        }
        if (colKeyRef.current && currentWidthRef.current !== startWidthRef.current) {
          store.setColumnWidth(colKeyRef.current, currentWidthRef.current);
        }
        colKeyRef.current = null;
        activeColElementRef.current = null;
        startXRef.current = 0;
        startWidthRef.current = 0;
        currentWidthRef.current = 0;
        store.endResizing();
        removeDocumentListeners();
      };
      const handleContextMenu = _temp;
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("contextmenu", handleContextMenu, true);
      cleanupRef.current = () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        document.removeEventListener("contextmenu", handleContextMenu, true);
      };
    };
    $[1] = colgroupRef;
    $[2] = getWidth;
    $[3] = store;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const onResizeStart = t1;
  let t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => removeDocumentListeners;
    t3 = [removeDocumentListeners];
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== isResizing || $[8] !== onResizeStart || $[9] !== resizingColumn) {
    t4 = {
      onResizeStart,
      isResizing,
      resizingColumn
    };
    $[7] = isResizing;
    $[8] = onResizeStart;
    $[9] = resizingColumn;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
function _temp(e_1) {
  e_1.preventDefault();
}